import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { API } from "@/lib/api";
import { ArrowRight, Check, X, ShieldCheck, Nfc, MapPin, Scale, Gavel, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { HeroBackground } from "@/components/HeroBackground";
import { Seo } from "@/components/Seo";
import { Header, Footer } from "./Landing";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, delay: d },
});
const SITE = "https://employguard.de";
const useL = () => (useTranslation().i18n.language || "de").slice(0, 2);
const pick = (obj, l) => obj[l] || obj.de;

function Shell({ children }) {
  return <div className="bg-background"><Header />{children}<Footer /></div>;
}

function Hero({ badge, title, sub, cta }) {
  return (
    <section className="hero-navy relative pb-20 pt-32 sm:pt-40">
      <HeroBackground />
      <div className="relative mx-auto max-w-4xl px-5 text-center">
        <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan" /> {badge}
        </motion.div>
        <motion.h1 {...fade(0.05)} className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">{title}</motion.h1>
        <motion.p {...fade(0.1)} className="mx-auto mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">{sub}</motion.p>
        <motion.div {...fade(0.15)} className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/register"><Button size="lg" data-testid="seo-primary-cta" className="w-full rounded-full bg-cyan px-7 text-white hover:bg-cyan-dark sm:w-auto">{cta} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
        </motion.div>
      </div>
    </section>
  );
}

function Prose({ blocks }) {
  return (
    <section className="mx-auto max-w-3xl px-5 py-16">
      {blocks.map((b, i) => (
        <motion.div key={i} {...fade(i * 0.03)} className="mb-8">
          {b.h && <h2 className="mb-3 font-heading text-2xl font-extrabold text-navy">{b.h}</h2>}
          {b.p && <p className="text-base leading-relaxed text-slate-600">{b.p}</p>}
          {b.list && (
            <ul className="mt-3 space-y-2">
              {b.list.map((li, j) => (
                <li key={j} className="flex items-start gap-2 text-slate-600"><Check className="mt-0.5 h-5 w-5 shrink-0 text-cyan-dark" /> <span>{li}</span></li>
              ))}
            </ul>
          )}
        </motion.div>
      ))}
    </section>
  );
}

function Faq({ title, items }) {
  return (
    <section className="mx-auto max-w-3xl px-5 pb-20">
      <h2 className="mb-6 font-heading text-2xl font-extrabold text-navy">{title}</h2>
      <div className="space-y-3">
        {items.map((f, i) => (
          <details key={i} className="group rounded-2xl border border-slate-200 bg-white p-5" data-testid={`seo-faq-${i}`}>
            <summary className="cursor-pointer list-none font-heading font-bold text-navy">{f.q}</summary>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function CrossLinks({ heading, links }) {
  return (
    <section className="border-t border-slate-100 bg-slate-50 py-14">
      <div className="mx-auto max-w-4xl px-5 text-center">
        <div className="mb-6 font-heading text-lg font-bold text-navy">{heading}</div>
        <div className="flex flex-wrap justify-center gap-3">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="rounded-full border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-navy transition-colors hover:border-cyan hover:text-cyan-dark" data-testid={`seo-crosslink-${l.to}`}>{l.label}</Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* =================================================== Content dictionaries */
const CROSS = {
  de: { h: "Mehr entdecken", z: "Zeiterfassung", n: "NFC-Zeiterfassung", s: "Subunternehmer-Compliance", f: "Ausländer-Compliance", pl: "Pflicht 2026", rg: "Ratgeber" },
  en: { h: "Explore more", z: "Time tracking", n: "NFC time tracking", s: "Subcontractor compliance", f: "Foreign-workforce compliance", pl: "Legal duty 2026", rg: "Guides" },
  bs: { h: "Istražite više", z: "Evidencija radnog vremena", n: "NFC evidencija", s: "Usklađenost podizvođača", f: "Usklađenost stranih radnika", pl: "Obaveza 2026", rg: "Vodiči" },
};
const crossLinks = (l, exclude = []) => [
  { to: "/zeiterfassung-pflicht-2026", label: pick(CROSS, l).pl },
  { to: "/nfc-zeiterfassung", label: pick(CROSS, l).n },
  { to: "/ratgeber", label: pick(CROSS, l).rg },
  { to: "/subunternehmer", label: pick(CROSS, l).s },
  { to: "/foreign-workforce", label: pick(CROSS, l).f },
].filter((x) => !exclude.includes(x.to));

const PFLICHT = {
  de: {
    title: "Arbeitszeiterfassung Pflicht 2026 — was Handwerk & Bau jetzt wissen müssen",
    desc: "Elektronische Zeiterfassung Pflicht 2026: gesetzliche Grundlage (BAG-Urteil, EuGH), GoBD-konforme & manipulationssichere Erfassung, Fristen und Bußgelder. So unterstützt EmployGuard Sie bei der rechtskonformen Umsetzung.",
    badge: "Recht & Compliance", cta: "Kostenlos starten",
    hero: "In Deutschland besteht die Pflicht zur systematischen Arbeitszeiterfassung. Erfahren Sie, was das für Handwerk, Bau und Subunternehmer bedeutet — und wie Sie es einfach umsetzen.",
    blocks: [
      { h: "Gibt es 2026 eine Pflicht zur Arbeitszeiterfassung?", p: "Ja. Nach dem EuGH-Urteil (2019) und dem BAG-Beschluss (2022, Az. 1 ABR 22/21) sind Arbeitgeber verpflichtet, Beginn, Ende und Dauer der täglichen Arbeitszeit zu erfassen. Für Bau- und andere Branchen gelten zusätzlich das Arbeitszeitgesetz (ArbZG), das Mindestlohngesetz (MiLoG) und das Schwarzarbeitsbekämpfungsgesetz (SchwarzArbG)." },
      { h: "Was muss die Zeiterfassung leisten?", list: [
        "Erfassung von Beginn, Ende und Pausen — objektiv, verlässlich und zugänglich.",
        "GoBD-konforme, revisionssichere und manipulationssichere Aufzeichnung.",
        "Aufbewahrung der Nachweise (MiLoG: mind. 2 Jahre; steuerlich oft länger).",
        "Bei Kontrollen durch die Finanzkontrolle Schwarzarbeit (FKS/Zoll) sofort vorlegbar.",
      ] },
      { h: "Welche Bußgelder drohen?", p: "Verstöße gegen Dokumentations- und Mindestlohnpflichten können mit Bußgeldern von bis zu 30.000 € (MiLoG) geahndet werden; bei Schwarzarbeit drohen weit höhere Sanktionen. Fehlende oder nachträglich manipulierte Nachweise sind bei FKS-Prüfungen ein zentrales Risiko." },
      { h: "So setzen Sie die Pflicht mit EmployGuard um", list: [
        "NFC/QR-Zeiterfassung direkt auf der Baustelle — ohne App-Installation.",
        "GPS-Standort & Geofence bei jeder Erfassung: manipulationssicher gegen Tag-Klonen.",
        "Automatische Prüfung von Pausen, Höchstarbeitszeit und Ruhezeiten (ArbZG).",
        "Zoll-/FKS-fertige Export (Arbeitszeitnachweis) mit einem Klick.",
      ] },
    ],
    faq: [
      { q: "Ab wann gilt die Zeiterfassungspflicht?", a: "Die Pflicht besteht bereits — sie folgt aus EuGH- und BAG-Rechtsprechung und dem ArbZG. Ein konkretisierendes Gesetz war in Vorbereitung; unabhängig davon müssen Arbeitgeber die Arbeitszeit heute schon vollständig erfassen." },
      { q: "Reicht ein Stundenzettel auf Papier?", a: "Papier ist zulässig, aber bei FKS-Prüfungen riskant, weil es leicht manipulierbar und schwer nachweisbar ist. Eine digitale, GoBD-konforme Lösung wie EmployGuard unterstützt Sie besser bei der Nachweispflicht." },
      { q: "Ist EmployGuard GoBD- und MiLoG-konform?", a: "Ja. Erfassungen sind revisionssicher, mit Zeitstempel und GPS-Standort versehen und werden fristgerecht aufbewahrt. Der Arbeitszeitnachweis lässt sich Zoll-fertig exportieren." },
      { q: "Funktioniert das ohne Smartphone-App?", a: "Ja. Der Arbeiter tippt den NFC-Tag an oder scannt den QR-Code auf der Baustelle — es öffnet sich eine Web-Seite, keine App-Installation nötig (Android & iPhone)." },
    ],
  },
  en: {
    title: "Time-tracking duty 2026 — what German trades & construction must know",
    desc: "Electronic time-tracking is mandatory in Germany (EuGH/BAG rulings). GoBD-compliant, tamper-proof recording, deadlines and fines — and how EmployGuard makes it simple.",
    badge: "Law & compliance", cta: "Start free",
    hero: "Germany requires systematic recording of working time. Learn what it means for trades, construction and subcontractors — and how to implement it easily.",
    blocks: [
      { h: "Is time tracking mandatory in 2026?", p: "Yes. Following the ECJ ruling (2019) and the German Federal Labour Court decision (2022), employers must record the start, end and duration of daily working time. Construction is additionally covered by the Working Time Act (ArbZG), the Minimum Wage Act (MiLoG) and anti-undeclared-work law (SchwarzArbG)." },
      { h: "What must time tracking deliver?", list: [
        "Record start, end and breaks — objectively, reliably and accessibly.",
        "GoBD-compliant, audit-proof and tamper-proof records.",
        "Retention of proof (MiLoG: at least 2 years; often longer for tax).",
        "Immediately presentable during customs / FKS (undeclared-work) audits.",
      ] },
      { h: "What fines apply?", p: "Breaches of documentation and minimum-wage duties can be fined up to €30,000 (MiLoG); undeclared work carries far higher penalties. Missing or manipulated records are a key risk during FKS audits." },
      { h: "How EmployGuard implements the duty", list: [
        "NFC/QR time tracking right on the Baustelle — no app install.",
        "GPS location & geofence on every clock-in: tamper-proof against tag cloning.",
        "Automatic checks of breaks, maximum hours and rest periods (ArbZG).",
        "Customs/FKS-ready export (working-time proof) in one click.",
      ] },
    ],
    faq: [
      { q: "When does the duty apply?", a: "It already applies — it follows from ECJ and Federal Labour Court case law and the ArbZG. Employers must fully record working time today." },
      { q: "Is a paper timesheet enough?", a: "Paper is allowed but risky during FKS audits because it is easy to manipulate and hard to prove. A digital, GoBD-compliant solution like EmployGuard is safer." },
      { q: "Is EmployGuard GoBD- and MiLoG-compliant?", a: "Yes. Records are audit-proof, time-stamped with GPS location and retained on time. The working-time proof exports customs-ready." },
      { q: "Does it work without a smartphone app?", a: "Yes. The worker taps the NFC tag or scans the QR code on site — a web page opens, no app install (Android & iPhone)." },
    ],
  },
  bs: {
    title: "Obaveza evidencije radnog vremena 2026 — šta zanatstvo i građevina moraju znati",
    desc: "Elektronska evidencija radnog vremena je obavezna u Njemačkoj (EuGH/BAG presude). GoBD-usklađena, evidencija otporna na manipulaciju, rokovi i kazne — i kako EmployGuard to pojednostavljuje.",
    badge: "Pravo i usklađenost", cta: "Započni besplatno",
    hero: "Njemačka zahtijeva sistematsku evidenciju radnog vremena. Saznajte šta to znači za zanatstvo, građevinu i podizvođače — i kako to lako provesti.",
    blocks: [
      { h: "Da li je evidencija obavezna u 2026?", p: "Da. Nakon presude EuGH (2019) i odluke saveznog radnog suda (2022), poslodavci moraju bilježiti početak, kraj i trajanje dnevnog radnog vremena. Građevinu dodatno pokrivaju ArbZG (zakon o radnom vremenu), MiLoG (minimalna plata) i SchwarzArbG (borba protiv rada na crno)." },
      { h: "Šta evidencija mora ispuniti?", list: [
        "Bilježenje početka, kraja i pauza — objektivno, pouzdano i dostupno.",
        "GoBD-usklađena evidencija otporna na reviziju i manipulaciju.",
        "Čuvanje dokaza (MiLoG: najmanje 2 godine; porezno često duže).",
        "Odmah dostupno pri kontroli carine / FKS (rad na crno).",
      ] },
      { h: "Koje kazne prijete?", p: "Kršenje obaveza dokumentacije i minimalne plate može se kazniti do 30.000 € (MiLoG); rad na crno nosi daleko veće sankcije. Nedostajući ili manipulisani zapisi su ključni rizik pri FKS kontrolama." },
      { h: "Kako EmployGuard provodi obavezu", list: [
        "NFC/QR evidencija direktno na gradilištu — bez instalacije aplikacije.",
        "GPS lokacija i geofence pri svakoj prijavi: otporno na kloniranje taga.",
        "Automatska provjera pauza, maksimalnog vremena i odmora (ArbZG).",
        "Izvoz spreman za carinu/FKS (dokaz radnog vremena) jednim klikom.",
      ] },
    ],
    faq: [
      { q: "Od kada vrijedi obaveza?", a: "Već vrijedi — proizlazi iz prakse EuGH i saveznog radnog suda te ArbZG. Poslodavci već danas moraju u potpunosti evidentirati radno vrijeme." },
      { q: "Je li papirni radni list dovoljan?", a: "Papir je dozvoljen ali rizičan pri FKS kontrolama jer se lako manipuliše i teško dokazuje. Digitalno, GoBD-usklađeno rješenje poput EmployGuarda je sigurnije." },
      { q: "Je li EmployGuard GoBD i MiLoG usklađen?", a: "Da. Zapisi su otporni na reviziju, s vremenskim žigom i GPS lokacijom te se čuvaju na vrijeme. Dokaz radnog vremena izvozi se spreman za carinu." },
      { q: "Radi li bez mobilne aplikacije?", a: "Da. Radnik tapne NFC tag ili skenira QR kod na gradilištu — otvori se web stranica, bez instalacije (Android i iPhone)." },
    ],
  },
};

const NFC = {
  de: {
    title: "NFC-Zeiterfassung für die Baustelle — manipulationssicher & ohne App",
    desc: "NFC-Zeiterfassung & QR-Stempeluhr für Bau und Handwerk: Arbeiter tippt den Tag an, GPS-Geofence prüft den Standort, Anti-Klon-Schutz verhindert Betrug. Ohne App, Android & iPhone.",
    badge: "NFC · QR · GPS", cta: "Kostenlos starten",
    hero: "Die moderne NFC-Stempeluhr für die Baustelle: antippen, fertig. Mit GPS-Geofence und Anti-Klon-Schutz wird jede Erfassung manipulationssicher — ganz ohne App-Installation.",
    blocks: [
      { h: "Was ist NFC-Zeiterfassung?", p: "Ein NFC-Baustellen-Tag (oder QR-Code) hängt an der Baustelle. Der Arbeiter hält sein Smartphone an den Tag — es öffnet sich eine Web-Seite und die Zeit wird erfasst. Keine App, keine Anmeldung mit App-Store, funktioniert auf Android und iPhone." },
      { h: "Manipulationssicher gegen Tag-Klonen", list: [
        "GPS-Standort bei jeder Erfassung — mit Geofence pro Baustelle.",
        "Anti-Klon-Heuristik blockiert unmögliche Erfassungen (z. B. gleicher Arbeiter zeitgleich an zwei Orten).",
        "Blockierte/verdächtige Erfassungen im Dashboard sichtbar und als CSV exportierbar.",
        "6-stelliger Mitarbeiter-PIN gegen fremde Nutzung.",
      ] },
      { h: "Warum ohne App?", p: "Bauarbeiter wechseln oft Geräte und Sprachen. Eine reine Web-Erfassung per NFC/QR senkt die Einstiegshürde auf null — kein Update, keine Installation, mehrsprachige Oberfläche." },
      { h: "Zoll- & GoBD-konform", p: "Jede Erfassung ist revisionssicher mit Zeitstempel und Standort. Der Arbeitszeitnachweis inkl. Standort/Kartenlink lässt sich für die Finanzkontrolle Schwarzarbeit (FKS) exportieren." },
    ],
    faq: [
      { q: "Brauchen Arbeiter eine App?", a: "Nein. Antippen des NFC-Tags oder QR-Scan öffnet eine Web-Seite. Keine App-Installation nötig — Android & iPhone." },
      { q: "Wie verhindert EmployGuard das Klonen von Tags?", a: "Über GPS-Geofence und eine Anti-Klon-Heuristik: Erfassungen von unmöglichen Orten (unrealistische Geschwindigkeit zwischen zwei Punkten) werden blockiert und protokolliert." },
      { q: "Was kostet ein NFC-Tag?", a: "Physische EmployGuard NFC-Baustellen-Tags kosten ab 2 € pro Tag. Ein Tag gilt für alle berechtigten Arbeiter auf dieser Baustelle." },
      { q: "Kann ich auch nur QR-Codes nutzen?", a: "Ja. Sie können QR-Codes selbst drucken (Poster/Etiketten) oder physische NFC-Tags bestellen — oder beides kombinieren." },
    ],
  },
  en: {
    title: "NFC time tracking for construction sites — tamper-proof & app-free",
    desc: "NFC time tracking & QR clock-in for construction and trades: tap the tag, GPS geofence verifies location, anti-clone protection prevents fraud. No app, Android & iPhone.",
    badge: "NFC · QR · GPS", cta: "Start free",
    hero: "The modern NFC clock for the Baustelle: tap and done. With GPS geofence and anti-clone protection, every clock-in is tamper-proof — with no app install.",
    blocks: [
      { h: "What is NFC time tracking?", p: "An NFC site tag (or QR code) is placed on site. The worker holds their phone to the tag — a web page opens and the time is recorded. No app, no app-store login, works on Android and iPhone." },
      { h: "Tamper-proof against tag cloning", list: [
        "GPS location on every clock-in — with a per-site geofence.",
        "Anti-clone heuristic blocks impossible clock-ins (same worker in two places at once).",
        "Blocked/suspicious clock-ins visible in the dashboard and exportable as CSV.",
        "6-digit worker PIN against misuse.",
      ] },
      { h: "Why app-free?", p: "Construction workers often switch devices and languages. Pure web capture via NFC/QR removes the barrier to entry — no updates, no install, multilingual UI." },
      { h: "Customs- & GoBD-compliant", p: "Every record is audit-proof with time-stamp and location. The working-time proof incl. location/map link exports for customs (FKS) inspections." },
    ],
    faq: [
      { q: "Do workers need an app?", a: "No. Tapping the NFC tag or scanning the QR opens a web page. No app install — Android & iPhone." },
      { q: "How does EmployGuard prevent tag cloning?", a: "Via GPS geofence and an anti-clone heuristic: clock-ins from impossible locations (unrealistic speed between two points) are blocked and logged." },
      { q: "What does an NFC tag cost?", a: "Physical EmployGuard NFC site tags start at €2 per tag. One tag covers all authorized workers on that site." },
      { q: "Can I use only QR codes?", a: "Yes. You can print QR codes yourself (posters/labels) or order physical NFC tags — or combine both." },
    ],
  },
  bs: {
    title: "NFC evidencija za gradilište — otporna na manipulaciju i bez aplikacije",
    desc: "NFC evidencija i QR prijava za građevinu i zanatstvo: radnik tapne tag, GPS geofence provjerava lokaciju, anti-klon zaštita sprečava prevaru. Bez aplikacije, Android i iPhone.",
    badge: "NFC · QR · GPS", cta: "Započni besplatno",
    hero: "Moderna NFC 'štempl-ura' za gradilište: tapni i gotovo. Uz GPS geofence i anti-klon zaštitu svaka prijava je otporna na manipulaciju — bez instalacije aplikacije.",
    blocks: [
      { h: "Šta je NFC evidencija?", p: "NFC tag gradilišta (ili QR kod) stoji na gradilištu. Radnik prisloni telefon uz tag — otvori se web stranica i vrijeme se evidentira. Bez aplikacije, bez prijave preko app-store-a, radi na Androidu i iPhoneu." },
      { h: "Otporno na kloniranje taga", list: [
        "GPS lokacija pri svakoj prijavi — s geofence-om po gradilištu.",
        "Anti-klon heuristika blokira nemoguće prijave (isti radnik na dva mjesta odjednom).",
        "Blokirane/sumnjive prijave vidljive u dashboardu i izvozive kao CSV.",
        "6-cifreni PIN radnika protiv zloupotrebe.",
      ] },
      { h: "Zašto bez aplikacije?", p: "Građevinski radnici često mijenjaju uređaje i jezike. Čisto web bilježenje preko NFC/QR uklanja prepreku — bez ažuriranja, bez instalacije, višejezični interfejs." },
      { h: "Usklađeno s carinom i GoBD", p: "Svaki zapis je otporan na reviziju s vremenskim žigom i lokacijom. Dokaz radnog vremena s lokacijom/linkom karte izvozi se za kontrolu (FKS)." },
    ],
    faq: [
      { q: "Trebaju li radnici aplikaciju?", a: "Ne. Tapkanje NFC taga ili skeniranje QR otvara web stranicu. Bez instalacije — Android i iPhone." },
      { q: "Kako EmployGuard sprečava kloniranje?", a: "Preko GPS geofence-a i anti-klon heuristike: prijave s nemogućih lokacija (nerealna brzina između tačaka) se blokiraju i bilježe." },
      { q: "Koliko košta NFC tag?", a: "Fizički EmployGuard NFC tagovi počinju od 2 € po tagu. Jedan tag vrijedi za sve ovlaštene radnike na tom gradilištu." },
      { q: "Mogu li koristiti samo QR kodove?", a: "Da. Možete sami printati QR kodove (posteri/naljepnice) ili naručiti fizičke NFC tagove — ili kombinovati." },
    ],
  },
};

const COMPETITORS = {
  clockin: "Clockin", papershift: "Papershift", timr: "timr", zep: "ZEP",
};
const CMP = {
  de: {
    badge: "Vergleich", cta: "EmployGuard kostenlos testen",
    title: (n) => `${n} Alternative — EmployGuard für Bau & ausländische Fachkräfte`,
    desc: (n) => `Auf der Suche nach einer ${n}-Alternative? EmployGuard verbindet NFC/QR-Zeiterfassung ohne App, GPS-Geofence, Subunternehmer- und Ausländer-Compliance (Aufenthaltstitel, A1) in einer Plattform.`,
    hero: (n) => `Sie suchen eine Alternative zu ${n}? EmployGuard ist speziell für Bau, Handwerk und den Einsatz ausländischer Fachkräfte gebaut — mit App-freier NFC-Zeiterfassung und integrierter Compliance.`,
    tableTitle: (n) => `EmployGuard vs. ${n}`,
    rows: [
      "NFC/QR-Zeiterfassung ohne App",
      "GPS-Standort & Geofence pro Baustelle",
      "Anti-Klon-Schutz gegen Zeitbetrug",
      "Subunternehmer-Compliance (§48b, Freistellungsbescheinigung)",
      "Ausländer-Compliance (Aufenthaltstitel, Arbeitserlaubnis, A1)",
      "Fristen & Dokumenten-Erinnerungen (SOKA-BAU, BG BAU)",
      "Zoll-/FKS-fertiger Arbeitszeitnachweis",
      "Mehrsprachiges Arbeiter-Portal",
    ],
    us: "EmployGuard", them: (n) => n, note: "Vergleich basierend auf öffentlich beworbenen Funktionen; Stand Änderungen vorbehalten. Prüfen Sie beim jeweiligen Anbieter den aktuellen Stand.",
    faqTitle: "Häufige Fragen",
    faq: (n) => [
      { q: `Warum EmployGuard statt ${n}?`, a: `EmployGuard ist auf Bau, Handwerk und ausländische Belegschaft spezialisiert: App-freie NFC/QR-Erfassung mit GPS-Geofence plus Subunternehmer- und Ausländer-Compliance in einer Plattform.` },
      { q: "Kann ich ohne App starten?", a: "Ja — Arbeiter tippen einen NFC-Tag an oder scannen einen QR-Code. Keine Installation, Android & iPhone." },
      { q: "Ist der Wechsel aufwändig?", a: "Nein. Mitarbeiter und Baustellen sind schnell angelegt (CSV-Import), NFC/QR-Tags erzeugen Sie in Minuten." },
    ],
  },
  en: {
    badge: "Comparison", cta: "Try EmployGuard free",
    title: (n) => `${n} alternative — EmployGuard for construction & foreign workforce`,
    desc: (n) => `Looking for a ${n} alternative? EmployGuard combines app-free NFC/QR time tracking, GPS geofence, and subcontractor & foreign-workforce compliance (residence permit, A1) in one platform.`,
    hero: (n) => `Looking for an alternative to ${n}? EmployGuard is purpose-built for construction, trades and foreign workforce — with app-free NFC time tracking and built-in compliance.`,
    tableTitle: (n) => `EmployGuard vs. ${n}`,
    rows: [
      "NFC/QR time tracking without an app",
      "GPS location & geofence per site",
      "Anti-clone protection against time fraud",
      "Subcontractor compliance (§48b, exemption certificate)",
      "Foreign-workforce compliance (residence permit, work permit, A1)",
      "Deadline & document reminders (SOKA-BAU, BG BAU)",
      "Customs/FKS-ready working-time proof",
      "Multilingual worker portal",
    ],
    us: "EmployGuard", them: (n) => n, note: "Comparison based on publicly advertised features; subject to change. Please verify current details with each provider.",
    faqTitle: "FAQ",
    faq: (n) => [
      { q: `Why EmployGuard instead of ${n}?`, a: `EmployGuard specializes in construction, trades and foreign workforce: app-free NFC/QR capture with GPS geofence plus subcontractor and foreign-workforce compliance in one platform.` },
      { q: "Can I start without an app?", a: "Yes — workers tap an NFC tag or scan a QR code. No install, Android & iPhone." },
      { q: "Is switching hard?", a: "No. Add employees and sites quickly (CSV import) and generate NFC/QR tags in minutes." },
    ],
  },
  bs: {
    badge: "Poređenje", cta: "Isprobaj EmployGuard besplatno",
    title: (n) => `${n} alternativa — EmployGuard za građevinu i strane radnike`,
    desc: (n) => `Tražite ${n} alternativu? EmployGuard spaja NFC/QR evidenciju bez aplikacije, GPS geofence te usklađenost podizvođača i stranih radnika (boravišna dozvola, A1) u jednoj platformi.`,
    hero: (n) => `Tražite alternativu za ${n}? EmployGuard je namjenski građen za građevinu, zanatstvo i strane radnike — s NFC evidencijom bez aplikacije i ugrađenom usklađenošću.`,
    tableTitle: (n) => `EmployGuard vs. ${n}`,
    rows: [
      "NFC/QR evidencija bez aplikacije",
      "GPS lokacija i geofence po gradilištu",
      "Anti-klon zaštita protiv prevare vremena",
      "Usklađenost podizvođača (§48b, potvrda o oslobođenju)",
      "Usklađenost stranih radnika (boravišna/radna dozvola, A1)",
      "Podsjetnici za rokove i dokumente (SOKA-BAU, BG BAU)",
      "Dokaz radnog vremena spreman za carinu/FKS",
      "Višejezični portal za radnike",
    ],
    us: "EmployGuard", them: (n) => n, note: "Poređenje na osnovu javno oglašenih funkcija; podložno promjenama. Provjerite aktuelno stanje kod svakog pružatelja.",
    faqTitle: "Česta pitanja",
    faq: (n) => [
      { q: `Zašto EmployGuard umjesto ${n}?`, a: `EmployGuard je specijalizovan za građevinu, zanatstvo i strane radnike: NFC/QR bez aplikacije s GPS geofence-om te usklađenost podizvođača i stranih radnika u jednoj platformi.` },
      { q: "Mogu li početi bez aplikacije?", a: "Da — radnici tapnu NFC tag ili skeniraju QR kod. Bez instalacije, Android i iPhone." },
      { q: "Je li prelazak težak?", a: "Nije. Radnike i gradilišta brzo dodate (CSV uvoz), a NFC/QR tagove generišete za par minuta." },
    ],
  },
};

const GUIDE_SLUGS = ["milog", "ruhezeiten", "scheinselbststaendigkeit", "a1-bescheinigung", "gobd-zeiterfassung", "fks-pruefung"];

const UPDATED_LABEL = { de: "Zuletzt aktualisiert", en: "Last updated", bs: "Zadnje ažurirano" };
const LOCALE = { de: "de-DE", en: "en-GB", bs: "bs-BA" };
const fmtDate = (iso, l) => {
  try { return new Date(iso).toLocaleDateString(LOCALE[l] || "de-DE", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
};

// E-E-A-T author/reviewer shown on all guides
const BLOG_AUTHOR = { name: "Sandy Smajić", url: "https://www.sandysmajic.com",
  title: { de: "Gründer & Compliance-Experte, EmployGuard", en: "Founder & compliance expert, EmployGuard", bs: "Osnivač i stručnjak za usklađenost, EmployGuard" } };
const BLOG_REVIEWER = "EmployGuard Compliance-Team";
const AUTHOR_LABEL = { de: "Von", en: "By", bs: "Autor" };
const REVIEW_LABEL = { de: "Geprüft von", en: "Reviewed by", bs: "Recenzirano od" };
const RELATED_LABEL = { de: "Weiterführende Ratgeber", en: "Related guides", bs: "Povezani vodiči" };
const STATIC_GUIDE_UPDATED = "2026-06-26T00:00:00Z";
const staticTitle = (slug, l) => (GUIDES[slug] ? pick(GUIDES[slug], l).title : null);

function Byline({ l, updated }) {
  return (
    <div className="mx-auto max-w-3xl px-5 pt-8" data-testid="guide-byline">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-slate-500">
        <span><span className="text-slate-400">{AUTHOR_LABEL[l]}:</span>{" "}
          <a href={BLOG_AUTHOR.url} target="_blank" rel="noopener noreferrer" className="font-semibold text-navy hover:text-cyan-dark">{BLOG_AUTHOR.name}</a>
          {" "}· {BLOG_AUTHOR.title[l]}</span>
        <span><span className="text-slate-400">{REVIEW_LABEL[l]}:</span> {BLOG_REVIEWER}</span>
        {updated && <span><span className="text-slate-400">{UPDATED_LABEL[l]}:</span> {updated}</span>}
      </div>
    </div>
  );
}

function RelatedGuides({ items, l }) {
  if (!items || !items.length) return null;
  return (
    <section className="border-t border-slate-100 bg-white py-14" data-testid="related-guides">
      <div className="mx-auto max-w-4xl px-5">
        <div className="mb-6 font-heading text-lg font-bold text-navy">{RELATED_LABEL[l]}</div>
        <div className="grid gap-4 sm:grid-cols-3">
          {items.map((it) => (
            <Link key={it.slug} to={`/ratgeber/${it.slug}`} data-testid={`related-${it.slug}`} className="group rounded-2xl border border-slate-200 bg-white p-5 transition-colors hover:border-cyan">
              <div className="font-heading text-sm font-bold leading-snug text-navy group-hover:text-cyan-dark">{it.title}</div>
              <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-cyan-dark">{readMoreLabel(l)} <ArrowRight className="h-3.5 w-3.5" /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

const GUIDES = {
  milog: {
    de: {
      badge: "Ratgeber · Mindestlohn", cta: "Zeiterfassung kostenlos starten",
      title: "MiLoG 2026: Mindestlohn, Dokumentationspflicht & Aufzeichnung",
      lead: "Mindestlohn, §17-Aufzeichnungspflicht, Aufbewahrung und Bußgelder — kompakt für Bau & Handwerk erklärt.",
      desc: "Mindestlohngesetz (MiLoG) einfach erklärt: aktueller Mindestlohn, Aufzeichnungspflicht der Arbeitszeit nach §17 MiLoG, Aufbewahrungsfristen, Bußgelder und wie digitale Zeiterfassung Sie bei der Einhaltung unterstützt.",
      blocks: [
        { h: "Was regelt das MiLoG?", p: "Das Mindestlohngesetz (MiLoG) legt den gesetzlichen Mindestlohn fest und verpflichtet Arbeitgeber bestimmter Branchen — insbesondere Bau — zur genauen Aufzeichnung der Arbeitszeit. Es dient der Bekämpfung von Lohndumping und Schwarzarbeit." },
        { h: "Aufzeichnungspflicht nach §17 MiLoG", list: [
          "Beginn, Ende und Dauer der täglichen Arbeitszeit müssen aufgezeichnet werden.",
          "Die Aufzeichnung ist spätestens bis zum Ablauf des 7. Tages nach der Arbeitsleistung zu erstellen.",
          "Nachweise sind mindestens 2 Jahre aufzubewahren und bei FKS-Prüfungen vorzulegen.",
        ] },
        { h: "Welche Bußgelder drohen?", p: "Verstöße gegen die Aufzeichnungspflicht können mit Bußgeldern bis 30.000 € geahndet werden; Unterschreitung des Mindestlohns sogar bis 500.000 €. Fehlende oder manipulierbare Papieraufzeichnungen sind das größte Risiko." },
        { h: "So unterstützt EmployGuard Ihre Compliance", list: [
          "Automatische, GoBD-konforme Erfassung von Beginn, Ende und Pausen.",
          "Revisionssicherer Audit-Trail — keine stille Nachbearbeitung.",
          "Zoll-/FKS-fertiger Arbeitszeitnachweis auf Knopfdruck.",
        ] },
      ],
      faq: [
        { q: "Für wen gilt die MiLoG-Aufzeichnungspflicht?", a: "Vor allem für die im SchwarzArbG §2a genannten Branchen (u. a. Bau, Gebäudereinigung, Gastronomie) sowie für Minijobber. Im Zweifel sollten Sie die Arbeitszeit immer vollständig erfassen." },
        { q: "Reicht Excel zur Dokumentation?", a: "Excel ist manipulierbar und bei FKS-Prüfungen riskant. Eine revisionssichere, digitale Lösung wie EmployGuard ist deutlich sicherer." },
        { q: "Wie lange muss ich Aufzeichnungen aufbewahren?", a: "Nach MiLoG mindestens 2 Jahre; steuer- und sozialversicherungsrechtlich häufig länger. EmployGuard bewahrt die Nachweise fristgerecht auf." },
      ],
    },
    en: {
      badge: "Guide · Minimum wage", cta: "Start time tracking free",
      title: "MiLoG 2026: minimum wage, documentation duty & recording",
      lead: "Minimum wage, §17 recording duty, retention and fines — explained for construction & trades.",
      desc: "The German Minimum Wage Act (MiLoG) explained: current minimum wage, the §17 duty to record working time, retention periods, fines, and how digital time tracking keeps you compliant.",
      blocks: [
        { h: "What does MiLoG cover?", p: "MiLoG sets the statutory minimum wage and requires employers in certain sectors — especially construction — to accurately record working time, fighting wage dumping and undeclared work." },
        { h: "Recording duty under §17 MiLoG", list: [
          "Start, end and duration of daily working time must be recorded.",
          "Records must be created within 7 days of the work being performed.",
          "Proof must be kept for at least 2 years and presented during FKS audits.",
        ] },
        { h: "What fines apply?", p: "Breaches of the recording duty can be fined up to €30,000; paying below minimum wage up to €500,000. Missing or manipulable paper records are the biggest risk." },
        { h: "Stay compliant with EmployGuard", list: [
          "Automatic, GoBD-compliant capture of start, end and breaks.",
          "Audit-proof trail — no silent edits.",
          "Customs/FKS-ready working-time proof in one click.",
        ] },
      ],
      faq: [
        { q: "Who does the MiLoG recording duty apply to?", a: "Mainly sectors listed in SchwarzArbG §2a (construction, cleaning, hospitality, etc.) and mini-jobbers. When in doubt, always record working time fully." },
        { q: "Is Excel enough?", a: "Excel is manipulable and risky during FKS audits. An audit-proof digital solution like EmployGuard is far safer." },
        { q: "How long must I keep records?", a: "Under MiLoG at least 2 years; often longer for tax and social security. EmployGuard retains proof on time." },
      ],
    },
    bs: {
      badge: "Vodič · Minimalna plata", cta: "Započni evidenciju besplatno",
      title: "MiLoG 2026: minimalna plata, obaveza dokumentacije i evidencija",
      lead: "Minimalna plata, §17 obaveza evidencije, čuvanje i kazne — objašnjeno za građevinu i zanatstvo.",
      desc: "Njemački zakon o minimalnoj plati (MiLoG) objašnjen: aktuelna minimalna plata, obaveza evidencije radnog vremena po §17, rokovi čuvanja, kazne i kako digitalna evidencija održava usklađenost.",
      blocks: [
        { h: "Šta reguliše MiLoG?", p: "MiLoG utvrđuje zakonsku minimalnu platu i obavezuje poslodavce u određenim djelatnostima — posebno građevini — na tačnu evidenciju radnog vremena, radi borbe protiv damping-plata i rada na crno." },
        { h: "Obaveza evidencije po §17 MiLoG", list: [
          "Početak, kraj i trajanje dnevnog radnog vremena moraju se evidentirati.",
          "Evidencija se mora izraditi u roku od 7 dana od obavljenog rada.",
          "Dokazi se čuvaju najmanje 2 godine i predočavaju pri FKS kontroli.",
        ] },
        { h: "Koje kazne prijete?", p: "Kršenje obaveze evidencije kažnjivo je do 30.000 €; isplata ispod minimalne plate do 500.000 €. Nedostajući ili promjenjivi papirni zapisi najveći su rizik." },
        { h: "Ostanite usklađeni s EmployGuardom", list: [
          "Automatsko, GoBD-usklađeno bilježenje početka, kraja i pauza.",
          "Trag revizije otporan na naknadne izmjene.",
          "Dokaz radnog vremena spreman za carinu/FKS jednim klikom.",
        ] },
      ],
      faq: [
        { q: "Na koga se odnosi obaveza po MiLoG?", a: "Uglavnom na djelatnosti iz SchwarzArbG §2a (građevina, čišćenje, ugostiteljstvo itd.) i mini-jobbere. U nedoumici, uvijek evidentirajte radno vrijeme u potpunosti." },
        { q: "Je li Excel dovoljan?", a: "Excel je promjenjiv i rizičan pri FKS kontroli. Digitalno rješenje otporno na reviziju poput EmployGuarda je znatno sigurnije." },
        { q: "Koliko dugo čuvati zapise?", a: "Po MiLoG najmanje 2 godine; porezno i za socijalno osiguranje često duže. EmployGuard čuva dokaze na vrijeme." },
      ],
    },
  },
  ruhezeiten: {
    de: {
      badge: "Ratgeber · Arbeitszeit", cta: "Pausen automatisch prüfen",
      title: "Ruhezeiten & Höchstarbeitszeit nach ArbZG — Pausen richtig erfassen",
      lead: "11 Stunden Ruhezeit, max. 8/10 Stunden Arbeit, Pausenregeln — und wie Sie Verstöße automatisch erkennen.",
      desc: "Ruhezeiten, Höchstarbeitszeit und Pausen nach dem Arbeitszeitgesetz (ArbZG) einfach erklärt: 11h Ruhezeit, 8/10h Höchstarbeitszeit, 30/45 Min. Pausen, Bußgelder — und automatische Prüfung mit EmployGuard.",
      blocks: [
        { h: "Die wichtigsten Regeln des ArbZG", list: [
          "Werktägliche Höchstarbeitszeit: 8 Stunden, verlängerbar auf 10 Stunden bei Ausgleich.",
          "Ruhepausen: mind. 30 Min. bei 6–9 Std., mind. 45 Min. bei über 9 Std.",
          "Ununterbrochene Ruhezeit von mind. 11 Stunden zwischen zwei Arbeitstagen.",
        ] },
        { h: "Warum das auf dem Bau oft schwierig ist", p: "Wechselnde Baustellen, Überstunden und vergessene Pausen-Abmeldungen führen leicht zu unbeabsichtigten Verstößen. Ohne systematische Erfassung fällt das erst bei einer Prüfung auf." },
        { h: "Bußgelder", p: "Verstöße gegen das ArbZG können mit Bußgeldern bis 30.000 € geahndet werden, bei Vorsatz drohen Straftatbestände. Der Arbeitgeber trägt die Verantwortung für die Einhaltung." },
        { h: "Automatische Prüfung mit EmployGuard", list: [
          "Automatischer Pausenabzug nach konfigurierbaren Regeln (>6h → 30 Min., >9h → 45 Min.).",
          "Warnungen bei Überschreitung der Höchstarbeitszeit und zu kurzer Ruhezeit.",
          "Compliance-Center mit Freigeben/Ablehnen je Zeiterfassung.",
        ] },
      ],
      faq: [
        { q: "Wie lange ist die gesetzliche Ruhezeit?", a: "Mindestens 11 Stunden ununterbrochen zwischen dem Ende eines Arbeitstages und dem Beginn des nächsten (§5 ArbZG)." },
        { q: "Wann sind welche Pausen Pflicht?", a: "Bei 6–9 Std. Arbeitszeit mind. 30 Min., bei mehr als 9 Std. mind. 45 Min. (§4 ArbZG)." },
        { q: "Prüft EmployGuard die Einhaltung automatisch?", a: "Ja. EmployGuard bewertet jede Schicht auf Pausen, Höchstarbeitszeit und Ruhezeit und markiert Auffälligkeiten zur Freigabe." },
      ],
    },
    en: {
      badge: "Guide · Working time", cta: "Check breaks automatically",
      title: "Rest periods & maximum hours under ArbZG — record breaks correctly",
      lead: "11h rest, max 8/10h work, break rules — and how to detect breaches automatically.",
      desc: "Rest periods, maximum working time and breaks under the German Working Time Act (ArbZG) explained: 11h rest, 8/10h maximum, 30/45 min breaks, fines — and automatic checks with EmployGuard.",
      blocks: [
        { h: "Key ArbZG rules", list: [
          "Daily maximum working time: 8 hours, extendable to 10 with compensation.",
          "Breaks: at least 30 min for 6–9 hours, at least 45 min above 9 hours.",
          "Uninterrupted rest of at least 11 hours between two working days.",
        ] },
        { h: "Why this is hard on site", p: "Changing sites, overtime and forgotten break clock-outs easily cause unintentional breaches. Without systematic recording it only surfaces during an audit." },
        { h: "Fines", p: "ArbZG breaches can be fined up to €30,000, with criminal liability for intent. The employer is responsible for compliance." },
        { h: "Automatic checks with EmployGuard", list: [
          "Automatic break deduction by configurable rules (>6h → 30 min, >9h → 45 min).",
          "Warnings for exceeding maximum hours and too-short rest.",
          "Compliance center with Approve/Reject per record.",
        ] },
      ],
      faq: [
        { q: "How long is the statutory rest period?", a: "At least 11 uninterrupted hours between the end of one working day and the start of the next (§5 ArbZG)." },
        { q: "When are breaks mandatory?", a: "For 6–9 hours at least 30 min, above 9 hours at least 45 min (§4 ArbZG)." },
        { q: "Does EmployGuard check compliance automatically?", a: "Yes. It evaluates each shift for breaks, maximum hours and rest, flagging anomalies for approval." },
      ],
    },
    bs: {
      badge: "Vodič · Radno vrijeme", cta: "Automatski provjeri pauze",
      title: "Odmori i maksimalno radno vrijeme po ArbZG — ispravno evidentiranje pauza",
      lead: "11h odmora, maks. 8/10h rada, pravila pauza — i kako automatski otkriti prekršaje.",
      desc: "Odmori, maksimalno radno vrijeme i pauze po njemačkom zakonu o radnom vremenu (ArbZG): 11h odmora, 8/10h maksimum, 30/45 min pauze, kazne — i automatske provjere s EmployGuardom.",
      blocks: [
        { h: "Ključna pravila ArbZG", list: [
          "Dnevno maksimalno radno vrijeme: 8 sati, produživo na 10 uz kompenzaciju.",
          "Pauze: najmanje 30 min za 6–9 sati, najmanje 45 min preko 9 sati.",
          "Neprekidni odmor od najmanje 11 sati između dva radna dana.",
        ] },
        { h: "Zašto je to teško na gradilištu", p: "Promjena gradilišta, prekovremeni i zaboravljene odjave pauza lako vode nenamjernim prekršajima. Bez sistematske evidencije to se otkrije tek pri kontroli." },
        { h: "Kazne", p: "Prekršaji ArbZG kažnjivi su do 30.000 €, uz krivičnu odgovornost pri namjeri. Poslodavac je odgovoran za usklađenost." },
        { h: "Automatske provjere s EmployGuardom", list: [
          "Automatski odbitak pauze po pravilima (>6h → 30 min, >9h → 45 min).",
          "Upozorenja pri prekoračenju maksimuma i prekratkom odmoru.",
          "Compliance centar s Odobri/Odbij po zapisu.",
        ] },
      ],
      faq: [
        { q: "Koliko traje zakonski odmor?", a: "Najmanje 11 neprekidnih sati između kraja jednog i početka sljedećeg radnog dana (§5 ArbZG)." },
        { q: "Kada su pauze obavezne?", a: "Za 6–9 sati najmanje 30 min, preko 9 sati najmanje 45 min (§4 ArbZG)." },
        { q: "Provjerava li EmployGuard usklađenost automatski?", a: "Da. Ocjenjuje svaku smjenu na pauze, maksimum i odmor te označava odstupanja za odobrenje." },
      ],
    },
  },
  scheinselbststaendigkeit: {
    de: {
      badge: "Ratgeber · Subunternehmer", cta: "Subunternehmer-Compliance testen",
      title: "Scheinselbstständigkeit bei Subunternehmern vermeiden — Kriterien & Nachweise",
      lead: "Kriterien, Risiken (SV-Nachzahlung) und die Dokumente, mit denen Sie auf der sicheren Seite sind.",
      desc: "Scheinselbstständigkeit im Bau vermeiden: Kriterien der DRV, Risiken (Nachzahlung von Sozialversicherungsbeiträgen), notwendige Nachweise (A1, Freistellungsbescheinigung, eigene Betriebsmittel) und wie EmployGuard hilft.",
      blocks: [
        { h: "Was ist Scheinselbstständigkeit?", p: "Von Scheinselbstständigkeit spricht man, wenn ein vermeintlich selbstständiger Subunternehmer faktisch wie ein Arbeitnehmer eingesetzt wird. Die Folge: Nachzahlung von Sozialversicherungsbeiträgen und ggf. strafrechtliche Konsequenzen für den Auftraggeber." },
        { h: "Typische Kriterien der DRV", list: [
          "Weisungsgebundenheit und feste Eingliederung in den Betrieb.",
          "Nur ein Auftraggeber, kein eigenes unternehmerisches Risiko.",
          "Keine eigenen Betriebsmittel, Arbeit unter fremdem Namen.",
        ] },
        { h: "Welche Nachweise schützen Sie?", list: [
          "A1-Bescheinigung bei entsandten Arbeitern (EU).",
          "Freistellungsbescheinigung nach §48b EStG (Bauabzugsteuer).",
          "Gewerbeanmeldung, eigene Betriebsmittel und mehrere Auftraggeber.",
        ] },
        { h: "So unterstützt EmployGuard", p: "Das Subunternehmer-Modul verwaltet A1, Freistellungsbescheinigung, Finanzamt-Zuordnung und Arbeiter-Dokumente zentral — mit Ablaufverfolgung, automatischen Erinnerungen und KI-Dokumentenanalyse. So sind alle Nachweise bei Prüfungen sofort griffbereit." },
      ],
      faq: [
        { q: "Wer haftet bei Scheinselbstständigkeit?", a: "In der Regel der Auftraggeber: Es drohen Nachzahlungen von Sozialversicherungsbeiträgen (bis zu 4 Jahre, bei Vorsatz länger) und mögliche strafrechtliche Folgen." },
        { q: "Reicht die A1-Bescheinigung allein?", a: "Nein. A1 belegt die Sozialversicherung im Heimatland, ersetzt aber nicht die Gesamtwürdigung. Sie sollten zusätzlich Freistellungsbescheinigung, Gewerbenachweise und unternehmerische Selbstständigkeit dokumentieren." },
        { q: "Wie hilft EmployGuard konkret?", a: "Alle relevanten Nachweise je Subunternehmer und Arbeiter liegen zentral vor, mit Fristen-Erinnerungen und Compliance-Status — ideal für die Vorlage bei DRV- oder Zoll-Prüfungen." },
      ],
    },
    en: {
      badge: "Guide · Subcontractors", cta: "Try subcontractor compliance",
      title: "Avoiding bogus self-employment with subcontractors — criteria & proof",
      lead: "Criteria, risks (social-security back-payment) and the documents that keep you safe.",
      desc: "Avoid bogus self-employment (Scheinselbstständigkeit) in construction: DRV criteria, risks (back-payment of social-security contributions), required proof (A1, exemption certificate, own equipment) and how EmployGuard helps.",
      blocks: [
        { h: "What is bogus self-employment?", p: "It occurs when a supposedly self-employed subcontractor is in fact used like an employee. The result: back-payment of social-security contributions and possible criminal consequences for the client." },
        { h: "Typical DRV criteria", list: [
          "Bound by instructions and firmly integrated into the business.",
          "Only one client, no genuine entrepreneurial risk.",
          "No own equipment, working under someone else's name.",
        ] },
        { h: "Which proof protects you?", list: [
          "A1 certificate for posted (EU) workers.",
          "Exemption certificate under §48b EStG (construction withholding tax).",
          "Trade registration, own equipment and multiple clients.",
        ] },
        { h: "How EmployGuard helps", p: "The subcontractor module centrally manages A1, exemption certificate, Finanzamt mapping and worker documents — with expiry tracking, automatic reminders and AI document analysis, so all proof is instantly ready during audits." },
      ],
      faq: [
        { q: "Who is liable for bogus self-employment?", a: "Usually the client: back-payment of social-security contributions (up to 4 years, longer for intent) and possible criminal consequences." },
        { q: "Is the A1 certificate alone enough?", a: "No. A1 proves social security in the home country but doesn't replace the overall assessment. Also document the exemption certificate, trade proof and genuine self-employment." },
        { q: "How does EmployGuard help specifically?", a: "All relevant proof per subcontractor and worker is centrally available with deadline reminders and compliance status — ideal for DRV or customs audits." },
      ],
    },
    bs: {
      badge: "Vodič · Podizvođači", cta: "Isprobaj usklađenost podizvođača",
      title: "Izbjegavanje prividne samostalnosti kod podizvođača — kriteriji i dokazi",
      lead: "Kriteriji, rizici (naknadna uplata doprinosa) i dokumenti koji vas štite.",
      desc: "Izbjegnite prividnu samostalnost (Scheinselbstständigkeit) u građevini: kriteriji DRV, rizici (naknadna uplata doprinosa za socijalno), potrebni dokazi (A1, potvrda o oslobođenju, vlastita sredstva) i kako EmployGuard pomaže.",
      blocks: [
        { h: "Šta je prividna samostalnost?", p: "Nastaje kada se navodno samostalni podizvođač zapravo koristi kao zaposlenik. Posljedica: naknadna uplata doprinosa za socijalno osiguranje i moguće krivične posljedice za naručioca." },
        { h: "Tipični kriteriji DRV", list: [
          "Vezanost uputama i čvrsta integracija u firmu.",
          "Samo jedan naručilac, bez stvarnog poduzetničkog rizika.",
          "Bez vlastitih sredstava rada, rad pod tuđim imenom.",
        ] },
        { h: "Koji dokazi vas štite?", list: [
          "A1 potvrda za upućene (EU) radnike.",
          "Potvrda o oslobođenju po §48b EStG (porez na građevinu).",
          "Registracija obrta, vlastita sredstva i više naručilaca.",
        ] },
        { h: "Kako EmployGuard pomaže", p: "Modul za podizvođače centralno vodi A1, potvrdu o oslobođenju, Finanzamt zaduženje i dokumente radnika — s praćenjem isteka, automatskim podsjetnicima i AI analizom dokumenata, pa su svi dokazi odmah spremni pri kontroli." },
      ],
      faq: [
        { q: "Ko odgovara za prividnu samostalnost?", a: "Obično naručilac: prijeti naknadna uplata doprinosa (do 4 godine, duže pri namjeri) i moguće krivične posljedice." },
        { q: "Je li A1 potvrda sama dovoljna?", a: "Ne. A1 dokazuje socijalno u matičnoj zemlji ali ne zamjenjuje ukupnu ocjenu. Dokumentujte i potvrdu o oslobođenju, dokaze o obrtu i stvarnu samostalnost." },
        { q: "Kako EmployGuard konkretno pomaže?", a: "Svi relevantni dokazi po podizvođaču i radniku centralno su dostupni s podsjetnicima i statusom usklađenosti — idealno za DRV ili carinske kontrole." },
      ],
    },
  },
  "a1-bescheinigung": {
    de: {
      badge: "Ratgeber · Entsendung", cta: "Ausländer-Compliance testen",
      title: "A1-Bescheinigung 2026: Pflicht, Antrag & Kontrolle bei Entsendung",
      lead: "Wann die A1-Bescheinigung Pflicht ist, wie Sie sie beantragen und was bei Zoll-Kontrollen zählt.",
      desc: "A1-Bescheinigung einfach erklärt: Pflicht bei EU-Entsendung, Antrag über die Krankenkasse/DRV, Gültigkeit, Mitführungspflicht auf der Baustelle und Bußgelder — plus wie EmployGuard Fristen überwacht.",
      blocks: [
        { h: "Was ist die A1-Bescheinigung?", p: "Die A1-Bescheinigung weist nach, in welchem EU-/EWR-Staat ein entsandter Arbeitnehmer sozialversichert ist. Sie verhindert doppelte Beitragspflicht und ist bei jedem grenzüberschreitenden Einsatz — auch tageweise — erforderlich." },
        { h: "Wann ist sie Pflicht?", list: [
          "Bei Entsendung von Arbeitnehmern aus einem anderen EU-/EWR-Staat nach Deutschland.",
          "Auch bei selbstständigen Subunternehmern, die grenzüberschreitend tätig sind.",
          "Vor Arbeitsaufnahme zu beantragen — rückwirkende Anträge sind riskant.",
        ] },
        { h: "Antrag & Gültigkeit", p: "Der Antrag läuft über den zuständigen Sozialversicherungsträger im Heimatland (in Deutschland: Krankenkasse bzw. DRV). Die A1 gilt für einen definierten Zeitraum und muss auf der Baustelle mitgeführt und bei der FKS/Zoll vorgelegt werden können." },
        { h: "Bußgelder & Risiken", p: "Fehlt die A1 bei einer Prüfung, drohen Bußgelder und Nachzahlungen von Sozialversicherungsbeiträgen — zudem ein Indiz für Scheinselbstständigkeit. Auftraggeber sollten die Vorlage vor Einsatzbeginn verlangen." },
        { h: "So hilft EmployGuard", list: [
          "Zentrale Ablage der A1 je Arbeiter und Subunternehmer.",
          "Automatische Fristen-Erinnerung vor Ablauf.",
          "Sofort vorlegbar bei Zoll-/FKS-Prüfungen.",
        ] },
      ],
      faq: [
        { q: "Braucht jeder entsandte Arbeiter eine eigene A1?", a: "Ja. Die A1 ist personenbezogen und für jeden entsandten Arbeitnehmer bzw. Selbstständigen einzeln zu beantragen und mitzuführen." },
        { q: "Gilt die A1 auch für kurze Einsätze?", a: "Ja. Bereits eintägige grenzüberschreitende Einsätze erfordern grundsätzlich eine A1-Bescheinigung." },
        { q: "Wer haftet, wenn die A1 fehlt?", a: "Neben dem entsendenden Unternehmen kann auch der deutsche Auftraggeber in die Haftung geraten — inklusive Nachzahlungen und Bußgeldern." },
      ],
    },
    en: {
      badge: "Guide · Posting", cta: "Try foreign-workforce compliance",
      title: "A1 certificate 2026: duty, application & inspection when posting",
      lead: "When the A1 certificate is mandatory, how to apply and what matters during customs audits.",
      desc: "The A1 certificate explained: mandatory for EU posting, application via health insurer/DRV, validity, carry-along duty on site and fines — plus how EmployGuard tracks deadlines.",
      blocks: [
        { h: "What is the A1 certificate?", p: "The A1 certificate proves in which EU/EEA state a posted worker is covered by social security. It prevents double contributions and is required for every cross-border assignment — even for a single day." },
        { h: "When is it mandatory?", list: [
          "When posting workers from another EU/EEA state to Germany.",
          "Also for self-employed subcontractors working across borders.",
          "Apply before work starts — retroactive applications are risky.",
        ] },
        { h: "Application & validity", p: "The application goes through the competent social-security body in the home country (in Germany: health insurer or DRV). The A1 is valid for a defined period and must be carried on site and presentable to FKS/customs." },
        { h: "Fines & risks", p: "If the A1 is missing during an inspection, fines and back-payment of social-security contributions can follow — and it is an indicator of bogus self-employment. Clients should require it before work begins." },
        { h: "How EmployGuard helps", list: [
          "Central storage of the A1 per worker and subcontractor.",
          "Automatic reminders before expiry.",
          "Instantly presentable during customs/FKS audits.",
        ] },
      ],
      faq: [
        { q: "Does every posted worker need their own A1?", a: "Yes. The A1 is personal and must be applied for and carried individually by each posted worker or self-employed person." },
        { q: "Does the A1 apply to short assignments?", a: "Yes. Even single-day cross-border assignments generally require an A1 certificate." },
        { q: "Who is liable if the A1 is missing?", a: "Besides the posting company, the German client can also become liable — including back-payments and fines." },
      ],
    },
    bs: {
      badge: "Vodič · Upućivanje", cta: "Isprobaj usklađenost stranih radnika",
      title: "A1 potvrda 2026: obaveza, zahtjev i kontrola pri upućivanju",
      lead: "Kada je A1 potvrda obavezna, kako je zatražiti i šta je bitno pri carinskoj kontroli.",
      desc: "A1 potvrda objašnjena: obavezna pri EU upućivanju, zahtjev preko zdravstvenog osiguranja/DRV, važenje, obaveza nošenja na gradilištu i kazne — te kako EmployGuard prati rokove.",
      blocks: [
        { h: "Šta je A1 potvrda?", p: "A1 potvrda dokazuje u kojoj je EU/EEA državi upućeni radnik socijalno osiguran. Sprečava dvostruke doprinose i potrebna je za svaki prekogranični angažman — čak i jednodnevni." },
        { h: "Kada je obavezna?", list: [
          "Pri upućivanju radnika iz druge EU/EEA države u Njemačku.",
          "Također za samostalne podizvođače koji rade prekogranično.",
          "Zatražiti prije početka rada — naknadni zahtjevi su rizični.",
        ] },
        { h: "Zahtjev i važenje", p: "Zahtjev ide preko nadležnog nosioca socijalnog osiguranja u matičnoj zemlji (u Njemačkoj: zdravstveno osiguranje ili DRV). A1 vrijedi za određeni period i mora se nositi na gradilištu te predočiti FKS/carini." },
        { h: "Kazne i rizici", p: "Ako A1 nedostaje pri kontroli, prijete kazne i naknadna uplata doprinosa — te je to pokazatelj prividne samostalnosti. Naručioci trebaju tražiti potvrdu prije početka rada." },
        { h: "Kako EmployGuard pomaže", list: [
          "Centralno pohranjivanje A1 po radniku i podizvođaču.",
          "Automatski podsjetnik prije isteka.",
          "Odmah dostupno pri carinskoj/FKS kontroli.",
        ] },
      ],
      faq: [
        { q: "Treba li svaki upućeni radnik vlastitu A1?", a: "Da. A1 je vezana za osobu i zahtijeva se te nosi pojedinačno za svakog upućenog radnika ili samostalca." },
        { q: "Vrijedi li A1 i za kratke angažmane?", a: "Da. Već jednodnevni prekogranični angažmani u pravilu zahtijevaju A1 potvrdu." },
        { q: "Ko odgovara ako A1 nedostaje?", a: "Osim firme koja upućuje, i njemački naručilac može odgovarati — uključujući naknadne uplate i kazne." },
      ],
    },
  },
  "gobd-zeiterfassung": {
    de: {
      badge: "Ratgeber · GoBD", cta: "GoBD-konform starten",
      title: "GoBD-konforme Zeiterfassung: Unveränderbarkeit & Revisionssicherheit",
      lead: "Was GoBD für die Arbeitszeiterfassung bedeutet — Unveränderbarkeit, Nachvollziehbarkeit und Aufbewahrung.",
      desc: "GoBD-konforme Zeiterfassung erklärt: Grundsätze der Unveränderbarkeit, lückenlose Protokollierung, Aufbewahrungsfristen und Verfahrensdokumentation — und wie EmployGuard revisionssicher erfasst.",
      blocks: [
        { h: "Was sind die GoBD?", p: "Die GoBD sind die Grundsätze zur ordnungsmäßigen Führung und Aufbewahrung von Büchern, Aufzeichnungen und Unterlagen in elektronischer Form. Sie gelten auch für digital erfasste Arbeitszeiten, sofern diese steuerlich relevant sind." },
        { h: "Kernanforderungen", list: [
          "Unveränderbarkeit: einmal erfasste Daten dürfen nicht spurlos geändert werden.",
          "Nachvollziehbarkeit & Vollständigkeit: lückenlose, zeitgerechte Erfassung.",
          "Ordnung und Aufbewahrung über die gesetzliche Frist.",
          "Verfahrensdokumentation des eingesetzten Systems.",
        ] },
        { h: "Warum Excel & Papier problematisch sind", p: "Excel-Tabellen und Papierzettel lassen sich unbemerkt ändern und erfüllen die Unveränderbarkeit meist nicht. Bei einer Betriebsprüfung oder FKS-Kontrolle kann das zu Beanstandungen führen." },
        { h: "So erfüllt EmployGuard die GoBD", list: [
          "Revisionssicherer Audit-Trail mit Zeitstempel je Erfassung.",
          "Korrekturen werden protokolliert statt still überschrieben.",
          "Fristgerechte Aufbewahrung und Zoll-fertiger Export.",
        ] },
      ],
      faq: [
        { q: "Gilt die GoBD auch für die Zeiterfassung?", a: "Ja, soweit die Aufzeichnungen steuerlich relevant sind (z. B. als Grundlage für Löhne). Die Grundsätze der Unveränderbarkeit und Nachvollziehbarkeit gelten entsprechend." },
        { q: "Ist eine Excel-Tabelle GoBD-konform?", a: "In der Regel nicht, da sie nachträglich unbemerkt geändert werden kann. Eine revisionssichere Softwarelösung ist vorzuziehen." },
        { q: "Wie lange muss ich Zeitaufzeichnungen aufbewahren?", a: "Je nach Zweck 2 Jahre (MiLoG) bis mehrere Jahre steuerlich. EmployGuard bewahrt die Nachweise fristgerecht auf." },
      ],
    },
    en: {
      badge: "Guide · GoBD", cta: "Start GoBD-compliant",
      title: "GoBD-compliant time tracking: immutability & audit-proof records",
      lead: "What GoBD means for time tracking — immutability, traceability and retention.",
      desc: "GoBD-compliant time tracking explained: principles of immutability, complete logging, retention periods and process documentation — and how EmployGuard records audit-proof.",
      blocks: [
        { h: "What are the GoBD?", p: "The GoBD are Germany's principles for proper keeping and retention of books, records and documents in electronic form. They also apply to digitally recorded working time where it is tax-relevant." },
        { h: "Core requirements", list: [
          "Immutability: recorded data must not be changed without a trace.",
          "Traceability & completeness: gap-free, timely recording.",
          "Order and retention for the statutory period.",
          "Process documentation of the system used.",
        ] },
        { h: "Why Excel & paper are problematic", p: "Spreadsheets and paper slips can be changed unnoticed and usually fail immutability. During a tax or FKS audit this can lead to objections." },
        { h: "How EmployGuard meets the GoBD", list: [
          "Audit-proof trail with a timestamp per record.",
          "Corrections are logged, not silently overwritten.",
          "Timely retention and customs-ready export.",
        ] },
      ],
      faq: [
        { q: "Do the GoBD apply to time tracking?", a: "Yes, where the records are tax-relevant (e.g. as a basis for wages). The principles of immutability and traceability apply accordingly." },
        { q: "Is an Excel sheet GoBD-compliant?", a: "Usually not, because it can be changed unnoticed afterwards. An audit-proof software solution is preferable." },
        { q: "How long must I keep time records?", a: "Depending on purpose, 2 years (MiLoG) up to several years for tax. EmployGuard retains proof on time." },
      ],
    },
    bs: {
      badge: "Vodič · GoBD", cta: "Započni GoBD-usklađeno",
      title: "GoBD-usklađena evidencija: nepromjenjivost i otpornost na reviziju",
      lead: "Šta GoBD znači za evidenciju radnog vremena — nepromjenjivost, sljedivost i čuvanje.",
      desc: "GoBD-usklađena evidencija objašnjena: načela nepromjenjivosti, potpuno bilježenje, rokovi čuvanja i dokumentacija postupka — te kako EmployGuard evidentira otporno na reviziju.",
      blocks: [
        { h: "Šta je GoBD?", p: "GoBD su njemačka načela urednog vođenja i čuvanja knjiga, zapisa i dokumenata u elektronskom obliku. Primjenjuju se i na digitalno evidentirano radno vrijeme kada je porezno relevantno." },
        { h: "Ključni zahtjevi", list: [
          "Nepromjenjivost: evidentirani podaci ne smiju se mijenjati bez traga.",
          "Sljedivost i potpunost: evidencija bez praznina i na vrijeme.",
          "Red i čuvanje tokom zakonskog roka.",
          "Dokumentacija postupka korištenog sistema.",
        ] },
        { h: "Zašto su Excel i papir problematični", p: "Tabele i papirni listići mogu se neprimjetno mijenjati i najčešće ne ispunjavaju nepromjenjivost. Pri poreznoj ili FKS kontroli to može dovesti do primjedbi." },
        { h: "Kako EmployGuard ispunjava GoBD", list: [
          "Trag revizije s vremenskim žigom po zapisu.",
          "Ispravke se bilježe umjesto tihog prepisivanja.",
          "Čuvanje na vrijeme i izvoz spreman za carinu.",
        ] },
      ],
      faq: [
        { q: "Primjenjuje li se GoBD na evidenciju vremena?", a: "Da, kada su zapisi porezno relevantni (npr. kao osnova za plate). Načela nepromjenjivosti i sljedivosti primjenjuju se u skladu s tim." },
        { q: "Je li Excel tabela GoBD-usklađena?", a: "U pravilu nije, jer se naknadno može neprimjetno promijeniti. Softversko rješenje otporno na reviziju je bolje." },
        { q: "Koliko dugo čuvati zapise vremena?", a: "Ovisno o svrsi, 2 godine (MiLoG) do više godina porezno. EmployGuard čuva dokaze na vrijeme." },
      ],
    },
  },
  "fks-pruefung": {
    de: {
      badge: "Ratgeber · Zoll/FKS", cta: "Prüfungssicher werden",
      title: "FKS-Prüfung Ablauf: Was der Zoll prüft & wie Sie vorbereitet sind",
      lead: "Ablauf einer FKS-Prüfung, welche Unterlagen der Zoll verlangt und wie Sie sofort vorlegen können.",
      desc: "FKS-Prüfung erklärt: Ablauf der Zollkontrolle (Finanzkontrolle Schwarzarbeit), Befugnisse, geforderte Unterlagen (Arbeitszeit, Mindestlohn, A1) und Bußgelder — plus Checkliste mit EmployGuard.",
      blocks: [
        { h: "Was ist die FKS?", p: "Die Finanzkontrolle Schwarzarbeit (FKS) ist eine Einheit des Zolls. Sie prüft die Einhaltung von Mindestlohn, Melde- und Aufzeichnungspflichten sowie die Bekämpfung illegaler Beschäftigung — besonders häufig im Baugewerbe." },
        { h: "So läuft eine Prüfung ab", list: [
          "Unangekündigtes Erscheinen auf der Baustelle, Personenbefragung und Identitätsprüfung.",
          "Abgleich der angetroffenen Personen mit Meldungen und Arbeitszeitnachweisen.",
          "Anforderung von Unterlagen (Arbeitszeit, Lohn, A1, Freistellungsbescheinigung).",
        ] },
        { h: "Welche Unterlagen der Zoll verlangt", list: [
          "Lückenlose Arbeitszeitnachweise (Beginn/Ende/Pausen).",
          "Nachweise über Mindestlohn und Lohnzahlungen.",
          "A1-Bescheinigungen und Aufenthalts-/Arbeitserlaubnisse.",
          "Bei Subunternehmern: Freistellungsbescheinigung nach §48b.",
        ] },
        { h: "Vorbereitet mit EmployGuard", p: "Alle Nachweise liegen digital, revisionssicher und je Baustelle/Arbeiter zentral vor. Arbeitszeitnachweise inkl. GPS-Standort und die Compliance-Dokumente lassen sich mit einem Klick exportieren — ideal für die sofortige Vorlage bei der FKS." },
      ],
      faq: [
        { q: "Kündigt der Zoll eine FKS-Prüfung an?", a: "In der Regel nicht. Baustellenkontrollen erfolgen meist unangekündigt, weshalb Nachweise jederzeit vorlegbar sein sollten." },
        { q: "Welche Bußgelder drohen bei Verstößen?", a: "Je nach Verstoß (Mindestlohn, Aufzeichnung, illegale Beschäftigung) bis zu 30.000 € bzw. 500.000 €; bei Schwarzarbeit auch strafrechtliche Folgen." },
        { q: "Wie bereite ich mich optimal vor?", a: "Lückenlose, revisionssichere Zeiterfassung, aktuelle A1/Aufenthaltstitel und §48b-Nachweise zentral bereithalten — mit EmployGuard sofort exportierbar." },
      ],
    },
    en: {
      badge: "Guide · Customs/FKS", cta: "Get audit-ready",
      title: "FKS audit process: what customs checks & how to be prepared",
      lead: "How an FKS audit works, which documents customs demands and how to present them instantly.",
      desc: "FKS audit explained: how the customs inspection (undeclared-work unit) runs, powers, required documents (working time, minimum wage, A1) and fines — plus a checklist with EmployGuard.",
      blocks: [
        { h: "What is the FKS?", p: "The FKS (Finanzkontrolle Schwarzarbeit) is a unit of German customs. It checks compliance with minimum wage, reporting and recording duties and fights illegal employment — especially often in construction." },
        { h: "How an audit runs", list: [
          "Unannounced arrival on site, interviews and identity checks.",
          "Matching people found against filings and working-time records.",
          "Request of documents (working time, wages, A1, exemption certificate).",
        ] },
        { h: "Documents customs demands", list: [
          "Gap-free working-time records (start/end/breaks).",
          "Proof of minimum wage and wage payments.",
          "A1 certificates and residence/work permits.",
          "For subcontractors: §48b exemption certificate.",
        ] },
        { h: "Prepared with EmployGuard", p: "All proof is stored digitally, audit-proof and centrally per site/worker. Working-time records incl. GPS location and compliance documents export in one click — ideal for instant presentation to the FKS." },
      ],
      faq: [
        { q: "Does customs announce an FKS audit?", a: "Usually not. Site inspections are mostly unannounced, so proof should be presentable at any time." },
        { q: "What fines apply for breaches?", a: "Depending on the breach (minimum wage, recording, illegal employment) up to €30,000 or €500,000; undeclared work also carries criminal consequences." },
        { q: "How do I best prepare?", a: "Keep gap-free, audit-proof time tracking, current A1/residence titles and §48b proof centrally available — instantly exportable with EmployGuard." },
      ],
    },
    bs: {
      badge: "Vodič · Carina/FKS", cta: "Budi spreman za kontrolu",
      title: "Tok FKS kontrole: šta carina provjerava i kako biti spreman",
      lead: "Kako teče FKS kontrola, koje dokumente carina traži i kako ih odmah predočiti.",
      desc: "FKS kontrola objašnjena: kako teče carinska kontrola (jedinica za rad na crno), ovlaštenja, traženi dokumenti (radno vrijeme, minimalna plata, A1) i kazne — te lista provjere s EmployGuardom.",
      blocks: [
        { h: "Šta je FKS?", p: "FKS (Finanzkontrolle Schwarzarbeit) je jedinica njemačke carine. Provjerava poštivanje minimalne plate, obaveza prijave i evidencije te suzbija ilegalno zapošljavanje — posebno često u građevini." },
        { h: "Kako teče kontrola", list: [
          "Nenajavljeni dolazak na gradilište, ispitivanje osoba i provjera identiteta.",
          "Upoređivanje zatečenih osoba s prijavama i evidencijom radnog vremena.",
          "Traženje dokumenata (radno vrijeme, plate, A1, potvrda o oslobođenju).",
        ] },
        { h: "Dokumenti koje carina traži", list: [
          "Evidencija radnog vremena bez praznina (početak/kraj/pauze).",
          "Dokazi o minimalnoj plati i isplatama.",
          "A1 potvrde i boravišne/radne dozvole.",
          "Za podizvođače: potvrda o oslobođenju po §48b.",
        ] },
        { h: "Spremni s EmployGuardom", p: "Svi dokazi pohranjeni su digitalno, otporno na reviziju i centralno po gradilištu/radniku. Evidencija radnog vremena s GPS lokacijom i dokumenti usklađenosti izvoze se jednim klikom — idealno za trenutno predočenje FKS-u." },
      ],
      faq: [
        { q: "Najavljuje li carina FKS kontrolu?", a: "U pravilu ne. Kontrole gradilišta su uglavnom nenajavljene, pa dokazi trebaju biti dostupni u svakom trenutku." },
        { q: "Koje kazne prijete pri prekršajima?", a: "Ovisno o prekršaju (minimalna plata, evidencija, ilegalni rad) do 30.000 € ili 500.000 €; rad na crno nosi i krivične posljedice." },
        { q: "Kako se najbolje pripremiti?", a: "Vodite evidenciju bez praznina i otpornu na reviziju, aktuelne A1/boravišne dozvole i §48b dokaze centralno — odmah izvozivo s EmployGuardom." },
      ],
    },
  },
};

const RATGEBER = {
  de: { badge: "Ratgeber", cta: "EmployGuard kostenlos starten",
    title: "EmployGuard Ratgeber — Compliance, Zeiterfassung & Bau-Recht",
    desc: "Praxis-Ratgeber für Handwerk & Bau: MiLoG, Ruhezeiten nach ArbZG, Scheinselbstständigkeit und mehr — verständlich erklärt und nachvollziehbar umgesetzt.",
    hero: "Verständliche Praxis-Leitfäden zu Arbeitszeit, Mindestlohn und Subunternehmer-Compliance — speziell für Handwerk und Bau." },
  en: { badge: "Guides", cta: "Start EmployGuard free",
    title: "EmployGuard Guides — compliance, time tracking & construction law",
    desc: "Practical guides for trades & construction: MiLoG, rest periods under ArbZG, bogus self-employment and more — clearly explained and compliantly implemented.",
    hero: "Clear, practical guides on working time, minimum wage and subcontractor compliance — built for trades and construction." },
  bs: { badge: "Vodiči", cta: "Započni EmployGuard besplatno",
    title: "EmployGuard vodiči — usklađenost, evidencija i građevinsko pravo",
    desc: "Praktični vodiči za zanatstvo i građevinu: MiLoG, odmori po ArbZG, prividna samostalnost i više — jasno objašnjeno i usklađeno provedeno.",
    hero: "Jasni, praktični vodiči o radnom vremenu, minimalnoj plati i usklađenosti podizvođača — za zanatstvo i građevinu." },
};

/* =================================================== Pages */
export function ZeiterfassungPflichtPage() {
  const l = useL(); const c = pick(PFLICHT, l);
  return (
    <Shell>
      <Seo title={c.title} desc={c.desc} path="/zeiterfassung-pflicht-2026" faq={c.faq}
        breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: c.badge, path: "/zeiterfassung-pflicht-2026" }]}
        schema={{ "@context": "https://schema.org", "@type": "Article", headline: c.title, description: c.desc, author: { "@type": "Organization", name: "EmployGuard" }, publisher: { "@type": "Organization", name: "EmployGuard" } }} />
      <Hero badge={c.badge} title={c.title} sub={c.hero} cta={c.cta} />
      <Prose blocks={c.blocks} />
      <Faq title={l === "de" ? "Häufige Fragen" : l === "bs" ? "Česta pitanja" : "FAQ"} items={c.faq} />
      <CrossLinks heading={pick(CROSS, l).h} links={crossLinks(l, ["/zeiterfassung-pflicht-2026"])} />
    </Shell>
  );
}

export function NfcZeiterfassungPage() {
  const l = useL(); const c = pick(NFC, l);
  return (
    <Shell>
      <Seo title={c.title} desc={c.desc} path="/nfc-zeiterfassung" faq={c.faq}
        breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: c.badge, path: "/nfc-zeiterfassung" }]}
        schema={{ "@context": "https://schema.org", "@type": "SoftwareApplication", name: "EmployGuard NFC Zeiterfassung", applicationCategory: "BusinessApplication", operatingSystem: "Web", description: c.desc, offers: { "@type": "Offer", price: "2500", priceCurrency: "EUR", category: "Add-on (annual)" } }} />
      <Hero badge={c.badge} title={c.title} sub={c.hero} cta={c.cta} />
      <Prose blocks={c.blocks} />
      <Faq title={l === "de" ? "Häufige Fragen" : l === "bs" ? "Česta pitanja" : "FAQ"} items={c.faq} />
      <CrossLinks heading={pick(CROSS, l).h} links={crossLinks(l, ["/nfc-zeiterfassung"])} />
    </Shell>
  );
}

export function ComparisonPage({ k }) {
  const l = useL(); const c = pick(CMP, l); const name = COMPETITORS[k] || k;
  const path = `/${k}-alternative`;
  const faq = c.faq(name);
  return (
    <Shell>
      <Seo title={c.title(name)} desc={c.desc(name)} path={path} faq={faq}
        breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: `${name} ${c.badge}`, path }]}
        schema={{ "@context": "https://schema.org", "@type": "Article", headline: c.title(name), description: c.desc(name), publisher: { "@type": "Organization", name: "EmployGuard" } }} />
      <Hero badge={c.badge} title={c.title(name)} sub={c.hero(name)} cta={c.cta} />
      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="mb-6 font-heading text-2xl font-extrabold text-navy">{c.tableTitle(name)}</h2>
        <div className="overflow-hidden rounded-2xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">Feature</th><th className="px-4 py-3 text-center text-cyan-dark">{c.us}</th><th className="px-4 py-3 text-center">{name}</th></tr>
            </thead>
            <tbody>
              {c.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`cmp-row-${i}`}>
                  <td className="px-4 py-3 text-slate-600">{r}</td>
                  <td className="px-4 py-3 text-center"><Check className="mx-auto h-5 w-5 text-emerald-500" /></td>
                  <td className="px-4 py-3 text-center"><X className="mx-auto h-4 w-4 text-slate-300" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-slate-400">{c.note}</p>
        <div className="mt-8 text-center"><Link to="/register"><Button className="rounded-full bg-cyan px-7 text-white hover:bg-cyan-dark">{c.cta} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link></div>
      </section>
      <Faq title={c.faqTitle} items={faq} />
      <CrossLinks heading={pick(CROSS, l).h} links={crossLinks(l)} />
    </Shell>
  );
}

const readMoreLabel = (l) => (l === "de" ? "Weiterlesen" : l === "bs" ? "Pročitaj više" : "Read more");

function GuideCard({ to, testid, badge, title, lead, l, i, updated, img }) {
  return (
    <motion.div {...fade(i * 0.05)}>
      <Link to={to} data-testid={testid} className="block h-full overflow-hidden rounded-2xl border border-slate-200 bg-white transition-colors hover:border-cyan">
        {img && <img src={img} alt="" loading="lazy" className="h-40 w-full object-cover" />}
        <div className="p-6">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-cyan-dark">{badge}</div>
          <h2 className="mb-2 font-heading text-xl font-extrabold text-navy">{title}</h2>
          <p className="text-sm leading-relaxed text-slate-600">{lead}</p>
          {updated && <div className="mt-3 text-xs text-slate-400">{UPDATED_LABEL[l]}: {updated}</div>}
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-cyan-dark">{readMoreLabel(l)} <ArrowRight className="h-4 w-4" /></span>
        </div>
      </Link>
    </motion.div>
  );
}

export function RatgeberHub() {
  const l = useL(); const c = pick(RATGEBER, l);
  const [aiPosts, setAiPosts] = useState([]);
  useEffect(() => {
    fetch(`${API}/blog/posts`).then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((d) => setAiPosts(d.posts || [])).catch(() => {});
  }, []);
  return (
    <Shell>
      <Seo title={c.title} desc={c.desc} path="/ratgeber"
        breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: c.badge, path: "/ratgeber" }]}
        schema={{ "@context": "https://schema.org", "@type": "CollectionPage", name: c.title, description: c.desc }} />
      <Hero badge={c.badge} title={c.title} sub={c.hero} cta={c.cta} />
      <section className="mx-auto max-w-4xl px-5 py-16">
        <div className="grid gap-5 sm:grid-cols-2">
          {GUIDE_SLUGS.map((slug, i) => {
            const g = pick(GUIDES[slug], l);
            return <GuideCard key={slug} to={`/ratgeber/${slug}`} testid={`ratgeber-card-${slug}`} badge={g.badge} title={g.title} lead={g.lead} l={l} i={i} />;
          })}
          {aiPosts.map((p, i) => {
            const g = (p.content && (p.content[l] || p.content.de || Object.values(p.content)[0])) || null;
            if (!g || !g.title) return null;
            return <GuideCard key={p.slug} to={`/ratgeber/${p.slug}`} testid={`ratgeber-card-${p.slug}`} badge={g.badge} title={g.title} lead={g.lead} l={l} i={GUIDE_SLUGS.length + i} updated={fmtDate(p.updated_at, l)} img={p.has_image ? `${API}/blog/image/${p.slug}` : null} />;
          })}
        </div>
      </section>
      <CrossLinks heading={pick(CROSS, l).h} links={crossLinks(l, ["/ratgeber"])} />
    </Shell>
  );
}

export function BlogPostPage() {
  const l = useL();
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found
  const [allPosts, setAllPosts] = useState([]);
  useEffect(() => {
    let alive = true;
    setPost(undefined);
    fetch(`${API}/blog/posts/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setPost(d); })
      .catch(() => { if (alive) setPost(null); });
    fetch(`${API}/blog/posts`)
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((d) => { if (alive) setAllPosts(d.posts || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  if (post === undefined) {
    return <Shell><section className="mx-auto max-w-3xl px-5 py-40 text-center text-slate-400" data-testid="blog-loading">…</section></Shell>;
  }
  const g = post && post.content ? (post.content[l] || post.content.de || Object.values(post.content)[0]) : null;
  if (!g || !g.title) {
    return <Shell><section className="mx-auto max-w-3xl px-5 py-40 text-center text-slate-500" data-testid="blog-not-found">{l === "de" ? "Nicht gefunden" : l === "bs" ? "Nije pronađeno" : "Not found"}</section></Shell>;
  }
  const path = `/ratgeber/${slug}`;
  const updated = fmtDate(post.updated_at, l);
  const coverUrl = post.has_image ? `${API}/blog/image/${slug}` : null;
  const relatedItems = (post.related || []).map((rs) => {
    const st = staticTitle(rs, l);
    if (st) return { slug: rs, title: st };
    const ap = allPosts.find((p) => p.slug === rs);
    if (ap && ap.content) { const ag = ap.content[l] || ap.content.de || Object.values(ap.content)[0]; return { slug: rs, title: (ag && ag.title) || rs }; }
    return null;
  }).filter(Boolean);
  return (
    <Shell>
      <Seo title={g.title} desc={g.desc || g.lead} path={path} faq={g.faq}
        breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: pick(RATGEBER, l).badge, path: "/ratgeber" }, { name: g.title, path }]}
        schema={{ "@context": "https://schema.org", "@type": "Article", headline: g.title, description: g.desc || g.lead, datePublished: post.published_at, dateModified: post.updated_at, ...(coverUrl ? { image: [coverUrl] } : {}), author: { "@type": "Person", name: BLOG_AUTHOR.name, url: BLOG_AUTHOR.url, jobTitle: BLOG_AUTHOR.title[l] }, reviewedBy: { "@type": "Organization", name: BLOG_REVIEWER }, publisher: { "@type": "Organization", name: "EmployGuard" } }} />
      <Hero badge={g.badge} title={g.title} sub={g.lead} cta={pick(RATGEBER, l).cta} />
      {coverUrl && (
        <div className="relative z-10 mx-auto -mt-10 max-w-4xl px-5">
          <img src={coverUrl} alt={g.title} data-testid="blog-cover" loading="lazy" className="aspect-[16/9] w-full rounded-2xl border border-slate-200 object-cover shadow-lg" />
        </div>
      )}
      <Byline l={l} updated={updated} />
      <Prose blocks={g.blocks} />
      {g.faq && g.faq.length ? <Faq title={l === "de" ? "Häufige Fragen" : l === "bs" ? "Česta pitanja" : "FAQ"} items={g.faq} /> : null}
      <RelatedGuides items={relatedItems} l={l} />
      <CrossLinks heading={pick(CROSS, l).h} links={crossLinks(l)} />
    </Shell>
  );
}

export function GuidePage({ slug }) {
  const l = useL(); const g = GUIDES[slug] ? pick(GUIDES[slug], l) : null;
  if (!g) return <Shell><section className="mx-auto max-w-3xl px-5 py-40 text-center text-slate-500">Not found</section></Shell>;
  const path = `/ratgeber/${slug}`;
  const relatedItems = GUIDE_SLUGS.filter((s) => s !== slug).slice(0, 3).map((s) => ({ slug: s, title: staticTitle(s, l) }));
  return (
    <Shell>
      <Seo title={g.title} desc={g.desc} path={path} faq={g.faq}
        breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: pick(RATGEBER, l).badge, path: "/ratgeber" }, { name: g.title, path }]}
        schema={{ "@context": "https://schema.org", "@type": "Article", headline: g.title, description: g.desc, dateModified: STATIC_GUIDE_UPDATED, author: { "@type": "Person", name: BLOG_AUTHOR.name, url: BLOG_AUTHOR.url, jobTitle: BLOG_AUTHOR.title[l] }, reviewedBy: { "@type": "Organization", name: BLOG_REVIEWER }, publisher: { "@type": "Organization", name: "EmployGuard" } }} />
      <Hero badge={g.badge} title={g.title} sub={g.lead} cta={g.cta} />
      <Byline l={l} updated={fmtDate(STATIC_GUIDE_UPDATED, l)} />
      <Prose blocks={g.blocks} />
      <Faq title={l === "de" ? "Häufige Fragen" : l === "bs" ? "Česta pitanja" : "FAQ"} items={g.faq} />
      <RelatedGuides items={relatedItems} l={l} />
      <CrossLinks heading={pick(CROSS, l).h} links={crossLinks(l)} />
    </Shell>
  );
}
